import { store } from 'redux/store';
import { useDispatch } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { format } from 'date-fns';

import { Operation } from 'types';

import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateOperation } from 'modules/transactions/redux/operationActions';

import { UpdateOperationModalContextType } from '../types/UpdateOperationModalContextType';
import { updateOperationModalInitValue } from '../constants/updateOperationModalInitValue';

const UpdateOperationModalContext =
  createContext<UpdateOperationModalContextType>(updateOperationModalInitValue);

export const UpdateOperationModlaProvider: FC<{
  children: ReactNode;
  operation?: Operation;
  hasNotarialRegistration: boolean;
}> = ({ children, operation, hasNotarialRegistration }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const date: string = format(
    new Date(operation?.date as string),
    'yyyy-MM-dd'
  );
  const [comments, setComments] = useState<string>(operation?.comments || '');
  const [notaryName, setNotaryName] = useState<string>(
    operation?.notarialRegistration?.notaryName || ''
  );
  const [notaryProtocol, setNotaryProtocol] = useState<string>(
    operation?.notarialRegistration?.protocolNr || ''
  );
  const [notaryCity, setNotaryCity] = useState<string>(
    operation?.notarialRegistration?.notaryCity || ''
  );
  const [hasBeenModified, setHasBeenModified] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeComments = (comment: string) => {
    setComments(comment);
    setHasBeenModified(true);
  };

  const handleChangeNotaryName = (value: string) => {
    setNotaryName(value);
    setHasBeenModified(true);
  };

  const handleChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
    setHasBeenModified(true);
  };

  const handleChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
    setHasBeenModified(true);
  };

  const handleUpdateOperation = async () => {
    try {
      setIsLoading(true);

      const operationData = {
        comments,
        notarialRegistration: {
          notaryName,
          notaryCity,
          protocolNr: notaryProtocol,
        },
      };

      await store.dispatch(
        updateOperation({
          operationId: operation?._id || '',
          updateOperationData: operationData,
        })
      );
      setIsLoading(false);

      successSwal(t('EditedOperationDataSuccesfully'));
    } catch (error) {
      errorSwal(t('ErrorWhileEditingOperationData'));
    } finally {
      handleCloseModal();
    }
  };

  return (
    <UpdateOperationModalContext.Provider
      value={{
        data: {
          operationType: operation?.operationType || '',
          date,
          comments,
          notaryName,
          notaryProtocol,
          notaryCity,
          hasBeenModified,
          hasNotarialRegistration,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeComments,
          handleChangeNotaryName,
          handleChangeNotaryProtocol,
          handleChangeNotaryCity,
          handleUpdateOperation,
        },
      }}
    >
      {children}
    </UpdateOperationModalContext.Provider>
  );
};

export const useUpdateOperationModal = () => {
  const context = useContext(UpdateOperationModalContext);
  if (!context) {
    throw new Error(
      'useUpdateOperationModal must be used within a UpdateOperationModalProvider'
    );
  }
  return context;
};
